/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '@framework/api-client';
import type { ErrorType } from '@framework/api-client';
import type {
  BEResponseCessazioniDTO,
  BEResponseDatiSiapDTO,
  BEResponseOrganicoIstitutoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const getMicOrganicoIstitutoCodAoo = (
  codAoo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseOrganicoIstitutoDTO>(
    {
      url: `/procedimenti-authentication-manager/mic/organico/istituto/${codAoo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMicOrganicoIstitutoCodAooQueryKey = (codAoo: string) =>
  [
    `/procedimenti-authentication-manager/mic/organico/istituto/${codAoo}`,
  ] as const;

export const getGetMicOrganicoIstitutoCodAooQueryOptions = <
  TData = Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>,
  TError = ErrorType<unknown>,
>(
  codAoo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMicOrganicoIstitutoCodAooQueryKey(codAoo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>
  > = ({ signal }) =>
    getMicOrganicoIstitutoCodAoo(codAoo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!codAoo, ...queryOptions };
};

export type GetMicOrganicoIstitutoCodAooQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>
>;
export type GetMicOrganicoIstitutoCodAooQueryError = ErrorType<unknown>;

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const useGetMicOrganicoIstitutoCodAoo = <
  TData = Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>,
  TError = ErrorType<unknown>,
>(
  codAoo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMicOrganicoIstitutoCodAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetMicOrganicoIstitutoCodAooQueryOptions(
    codAoo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento = (
  codAoo: string,
  dataRiferimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseOrganicoIstitutoDTO>(
    {
      url: `/procedimenti-authentication-manager/mic/organico/IstitutoAndDataRiferimento/${codAoo}/${dataRiferimento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryKey =
  (codAoo: string, dataRiferimento: string) =>
    [
      `/procedimenti-authentication-manager/mic/organico/IstitutoAndDataRiferimento/${codAoo}/${dataRiferimento}`,
    ] as const;

export const getGetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    codAoo: string,
    dataRiferimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<
        typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryKey(
        codAoo,
        dataRiferimento,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
        >
      >
    > = ({ signal }) =>
      getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento(
        codAoo,
        dataRiferimento,
        requestOptions,
        signal,
      );

    return {
      queryKey,
      queryFn,
      enabled: !!(codAoo && dataRiferimento),
      ...queryOptions,
    };
  };

export type GetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
      >
    >
  >;
export type GetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryError =
  ErrorType<unknown>;

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const useGetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento =
  <
    TData = Awaited<
      ReturnType<
        typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
      >
    >,
    TError = ErrorType<unknown>,
  >(
    codAoo: string,
    dataRiferimento: string,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimento
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof apiClientRequest>;
    },
  ): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const queryOptions =
      getGetMicOrganicoIstitutoAndDataRiferimentoCodAooDataRiferimentoQueryOptions(
        codAoo,
        dataRiferimento,
        options,
      );

    const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
      queryKey: QueryKey;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
  };

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const getMicOrganicoCessazioniCodAooDataInizioDataFine = (
  codAoo: string,
  dataInizio: string,
  dataFine: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseCessazioniDTO>(
    {
      url: `/procedimenti-authentication-manager/mic/organico/Cessazioni/${codAoo}/${dataInizio}/${dataFine}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMicOrganicoCessazioniCodAooDataInizioDataFineQueryKey = (
  codAoo: string,
  dataInizio: string,
  dataFine: string,
) =>
  [
    `/procedimenti-authentication-manager/mic/organico/Cessazioni/${codAoo}/${dataInizio}/${dataFine}`,
  ] as const;

export const getGetMicOrganicoCessazioniCodAooDataInizioDataFineQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>
  >,
  TError = ErrorType<unknown>,
>(
  codAoo: string,
  dataInizio: string,
  dataFine: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetMicOrganicoCessazioniCodAooDataInizioDataFineQueryKey(
      codAoo,
      dataInizio,
      dataFine,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>>
  > = ({ signal }) =>
    getMicOrganicoCessazioniCodAooDataInizioDataFine(
      codAoo,
      dataInizio,
      dataFine,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(codAoo && dataInizio && dataFine),
    ...queryOptions,
  };
};

export type GetMicOrganicoCessazioniCodAooDataInizioDataFineQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>>
  >;
export type GetMicOrganicoCessazioniCodAooDataInizioDataFineQueryError =
  ErrorType<unknown>;

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const useGetMicOrganicoCessazioniCodAooDataInizioDataFine = <
  TData = Awaited<
    ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>
  >,
  TError = ErrorType<unknown>,
>(
  codAoo: string,
  dataInizio: string,
  dataFine: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getMicOrganicoCessazioniCodAooDataInizioDataFine>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetMicOrganicoCessazioniCodAooDataInizioDataFineQueryOptions(
      codAoo,
      dataInizio,
      dataFine,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @deprecated
 * @summary Ottiene i dati del dipendente mic da siap
 */
export const getMicDatiDipendente = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseDatiSiapDTO>(
    {
      url: `/procedimenti-authentication-manager/mic/dati-dipendente`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMicDatiDipendenteQueryKey = () =>
  [`/procedimenti-authentication-manager/mic/dati-dipendente`] as const;

export const getGetMicDatiDipendenteQueryOptions = <
  TData = Awaited<ReturnType<typeof getMicDatiDipendente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMicDatiDipendente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getMicDatiDipendente>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMicDatiDipendenteQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicDatiDipendente>>
  > = ({ signal }) => getMicDatiDipendente(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetMicDatiDipendenteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMicDatiDipendente>>
>;
export type GetMicDatiDipendenteQueryError = ErrorType<unknown>;

/**
 * @deprecated
 * @summary Ottiene i dati del dipendente mic da siap
 */
export const useGetMicDatiDipendente = <
  TData = Awaited<ReturnType<typeof getMicDatiDipendente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMicDatiDipendente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetMicDatiDipendenteQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const getMicDatiDipendenteDataRiferimento = (
  dataRiferimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<BEResponseDatiSiapDTO>(
    {
      url: `/procedimenti-authentication-manager/mic/dati-dipendente/${dataRiferimento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMicDatiDipendenteDataRiferimentoQueryKey = (
  dataRiferimento: string,
) =>
  [
    `/procedimenti-authentication-manager/mic/dati-dipendente/${dataRiferimento}`,
  ] as const;

export const getGetMicDatiDipendenteDataRiferimentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>,
  TError = ErrorType<unknown>,
>(
  dataRiferimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetMicDatiDipendenteDataRiferimentoQueryKey(dataRiferimento);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>
  > = ({ signal }) =>
    getMicDatiDipendenteDataRiferimento(
      dataRiferimento,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!dataRiferimento, ...queryOptions };
};

export type GetMicDatiDipendenteDataRiferimentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>
>;
export type GetMicDatiDipendenteDataRiferimentoQueryError = ErrorType<unknown>;

/**
 * @summary Consente di recuperare l'organico dell'istituto indicato
 */
export const useGetMicDatiDipendenteDataRiferimento = <
  TData = Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>,
  TError = ErrorType<unknown>,
>(
  dataRiferimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMicDatiDipendenteDataRiferimento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetMicDatiDipendenteDataRiferimentoQueryOptions(
    dataRiferimento,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
